// frontend/src/core/auth/ImpersonationBanner.tsx
//
// Fixed, top-of-viewport banner shown for the duration of an admin
// impersonation session. It reads the impersonation claim straight off the
// current JWT (via decodeImp — display-only, not a trust boundary) so it
// renders on every authenticated screen while impersonating and never
// otherwise. Ticks a mm:ss countdown from the token's `exp` and auto-exits
// at zero, using the same path as the manual Exit button.
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { getToken } from '../api-client';
import { decodeImp, takeStashedAdminToken } from './impersonation';
import { cn } from '../../lib/utils';

/** Format a whole number of seconds as zero-padded `mm:ss` (floored at 0). */
function formatMmSs(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

export function ImpersonationBanner() {
  const { identity, adoptToken, logout } = useAuth();
  const navigate = useNavigate();

  const token = getToken() ?? '';
  const claims = decodeImp(token);

  const [nowMs, setNowMs] = React.useState<number>(() => Date.now());
  const [exiting, setExiting] = React.useState(false);
  // A ref (not just the `exiting` state) so a same-tick re-entrant call —
  // e.g. a double-click, or the auto-exit effect firing before the click
  // handler's state update has flushed — sees the guard synchronously
  // rather than racing on a stale render's closure.
  const exitingRef = React.useRef(false);

  // Tick every second only while an impersonation session is active.
  React.useEffect(() => {
    if (!claims) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [claims?.exp]);

  const exit = React.useCallback(async () => {
    if (exitingRef.current) return;
    exitingRef.current = true;
    setExiting(true);
    const stashed = takeStashedAdminToken();
    if (stashed) {
      try {
        await adoptToken(stashed);
        navigate('/admin/tenants');
        return;
      } catch {
        // Adopting the stashed admin token failed — fall through to a full
        // logout rather than leaving the admin stuck mid-impersonation.
      }
    }
    logout();
  }, [adoptToken, logout, navigate]);

  const remainingSeconds = claims ? Math.max(0, (claims.exp * 1000 - nowMs) / 1000) : 0;

  // Auto-exit the instant the countdown hits zero.
  React.useEffect(() => {
    if (!claims) return;
    if (remainingSeconds <= 0) {
      void exit();
    }
  }, [claims?.exp, remainingSeconds <= 0]);

  if (!claims) return null;

  const label = identity && identity.type === 'user'
    ? (identity.name || identity.email)
    : claims.admin_label;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed top-0 inset-x-0 z-[200] flex items-center justify-center gap-2.5',
        'h-10 px-4 bg-rose-600 text-white text-[13px] font-semibold tracking-[-0.005em]',
        'shadow-[0_4px_16px_-4px_rgba(0,0,0,0.35)]',
      )}
    >
      <ShieldAlert className="h-4 w-4 shrink-0" strokeWidth={2.2} />
      <span className="truncate">
        Viewing as {label} (via {claims.admin_label}) — expires in {formatMmSs(remainingSeconds)}
      </span>
      <span aria-hidden="true" className="opacity-60">·</span>
      <button
        type="button"
        onClick={() => { void exit(); }}
        disabled={exiting}
        className="underline underline-offset-2 hover:text-white/85 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        Exit
      </button>
    </div>
  );
}

export default ImpersonationBanner;
